import { Box, Skeleton, Stack } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { DRAWER_WIDTH } from "../drawer/DrawerContent"
import { ShopListSkeleton } from "../shop/ShopListSkeleton"

const ACCORDION_BORDER_RADIUS = ".75rem"
const COLLAPSED_SECTION_COUNT = 2

const useStyles = makeStyles()((theme) => ({
    // the single-pane layout opens on the products
    drawer: {
        [theme.breakpoints.down("md")]: {
            display: "none",
        },
        borderLeft: `1px solid ${theme.palette.tailwind.slate[300]}`,
        background: theme.palette.common.white,
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
    },
    drawerSections: {
        display: "flex",
        flexDirection: "column",
        gap: ".75rem",
        overflow: "hidden",
        flexGrow: 1,
    },
    infoBanner: {
        transform: "none",
        borderRadius: ".5rem",
        flexShrink: 0,
    },
    accordion: {
        borderRadius: ACCORDION_BORDER_RADIUS,
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        padding: "1rem 0",
        flexShrink: 0,
    },
    accordionHeader: {
        display: "grid",
        gridTemplateColumns: "2.5rem 1fr 1.5rem",
        alignItems: "center",
        gap: ".75rem",
        padding: "0 1rem",
    },
    accordionBadge: {
        transform: "none",
        borderRadius: "50%",
    },
    accordionTitle: {
        transform: "none",
        borderRadius: ".375rem",
    },
    accordionChevron: {
        transform: "none",
        borderRadius: ".25rem",
    },
    accordionContent: {
        padding: "1rem 1rem 0",
        display: "grid",
        gap: "1rem",
    },
    inputField: {
        transform: "none",
        borderRadius: ".375rem",
    },
    repeatLink: {
        transform: "none",
        borderRadius: ".375rem",
    },
    submitButton: {
        transform: "none",
        borderRadius: ".375rem",
        flexShrink: 0,
    },
}))

function DrawerAccordionSkeleton({ expanded = false }: Readonly<{ expanded?: boolean }>) {
    const { classes } = useStyles()

    return (
        <Box className={classes.accordion}>
            <Box className={classes.accordionHeader}>
                <Skeleton
                    variant="circular"
                    animation="wave"
                    width="2rem"
                    height="2rem"
                    className={classes.accordionBadge}
                />
                <Skeleton
                    variant="rounded"
                    animation="wave"
                    width="60%"
                    height="1.125rem"
                    className={classes.accordionTitle}
                />
                <Skeleton
                    variant="rounded"
                    animation="wave"
                    width="1rem"
                    height="1rem"
                    className={classes.accordionChevron}
                />
            </Box>
            {expanded && (
                <Box className={classes.accordionContent}>
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        width="100%"
                        height="3.5rem"
                        className={classes.inputField}
                    />
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        width="100%"
                        height="3.5rem"
                        className={classes.inputField}
                    />
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        width="100%"
                        height="3.5rem"
                        className={classes.inputField}
                    />
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        width="100%"
                        height="3.5rem"
                        className={classes.inputField}
                    />
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        width="50%"
                        height="1rem"
                        className={classes.repeatLink}
                    />
                </Box>
            )}
        </Box>
    )
}

export function MeetingServiceFormSkeleton() {
    const { classes } = useStyles()

    return (
        <Box display="grid" height="100%" gridTemplateColumns={{ xs: "minmax(0, 1fr)", md: `1fr ${DRAWER_WIDTH}` }}>
            <ShopListSkeleton />
            <Stack className={classes.drawer}>
                <Stack className={classes.drawerSections}>
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        width="100%"
                        height="4rem"
                        className={classes.infoBanner}
                    />
                    <DrawerAccordionSkeleton expanded />
                    {Array.from({ length: COLLAPSED_SECTION_COUNT }).map((_, i) => (
                        <DrawerAccordionSkeleton key={i} />
                    ))}
                </Stack>
                <Skeleton
                    variant="rounded"
                    animation="wave"
                    width="100%"
                    height="2.75rem"
                    className={classes.submitButton}
                />
            </Stack>
        </Box>
    )
}
